import { Component, Host, h, Element, Prop, State, Watch, Fragment } from '@stencil/core';
import { createCustomWebComponent } from 'custom-loader/utils';
import Modals from '../chat-assistance/common/Modals';
import state from 'store';
import reelsModal from 'store/reelsModal';
import galleryModal from 'store/galleryModal';
import flightDetailModal from 'store/flightDetailModal';
import Toast from 'eva-components/Toast';
import toast from 'store/toast';
import { changeOverflowBody, triggerResize } from 'utils/ui';
import { trackEvent } from 'utils/tracker';
import { i18n } from 'i18n';
import { getProviderConfiguration, providerConfState } from 'store/providerConf';
import { PropsComponentHelper } from 'utils/propsComponent';
import { GlobaConfig, WebComponentProps } from 'types';
import initParams from 'store/initParams';

@Component({
  tag: 'chat-assistance-floating',
  styleUrl: 'chat-assistance-floating.scss',
  shadow: false,
  scoped: true,
})
export class ChatAssistanceFloating {
  @Element() el: HTMLElement;
  @Prop() open: string = 'false';

  @State() entityHelper: PropsComponentHelper<WebComponentProps>;
  @State() styles = {};
  @State() isOpen = false;

  // Esto es necesario para evitar la mutacion forzada cuando abre el chat, sino pierde el reconocimiento si es mobile o no
  @State() isMobile = initParams.isMobile;

  @Watch('isOpen')
  private changeIsOpen() {
    const fnTracking = () =>
      trackEvent({
        event_type: 'user',
        event_name: this.isOpen ? 'click_open_globa' : 'click_close_globa',
      });

    if (initParams.assistantId === '') {
      window.document.addEventListener('chat-assistance-loaded', fnTracking);
    } else {
      fnTracking();
    }
  }

  private toggleChat() {
    const chatContenedor = this.el.querySelector('#chat-floating .chat-container') as HTMLDivElement;
    const chatWrapper = this.el.querySelector('#chat-floating .chat-header-content-wrapper') as HTMLDivElement;

    if (chatContenedor !== null && chatWrapper !== null) {
      const { channel, assistantId, pageViewId, version, sandbox, site, locale, configString, initMessage } = this.entityHelper.getProps();

      this.styles = this.getChatFloatingHeight();
      chatWrapper.classList.toggle('open');
      this.isOpen = !this.isOpen;

      const fullScreenChat = this.isFullScreenChat(true);
      changeOverflowBody(fullScreenChat, false);

      if (chatContenedor.getAttribute('loaded') !== 'true') {
        createCustomWebComponent(
          'chat-assistance',
          {
            channel,
            site,
            locale,
            pageViewId,
            mode: 'BUBBLE',
            sandbox,
            version,
            assistantId,
            configString,
            initMessage,
          },
          chatContenedor,
        );
        chatContenedor.setAttribute('loaded', 'true');
      }

      // If is open, trigger resize to recalculate floating section position
      if (this.isOpen) {
        triggerResize();
      }
    } else {
      console.error("Can't find chat-floating or chat-container");
    }
  }

  private getChatFloatingHeight() {
    const { globa } = this.entityHelper.getProps();

    const GAP_TO_TOP = 10;
    const GAP_BETWEEN_GLOBA_CHAT = 10;
    const CLOSE_BUTTON_HEIGHT = 45;
    const fullScreenChat = this.isFullScreenChat(false);
    // If open left, need add globa height to height
    const startHeight =
      this.resolveOpenPosition(globa) === 'top' ? this.el.offsetTop - GAP_BETWEEN_GLOBA_CHAT - CLOSE_BUTTON_HEIGHT : this.el.offsetTop + this.el.clientHeight - CLOSE_BUTTON_HEIGHT;
    const heightContainer = startHeight - GAP_TO_TOP + 'px';

    const widthContainer = (fullScreenChat ? window.innerWidth : Math.min(400, this.el.offsetLeft)) + 'px';

    return fullScreenChat
      ? {}
      : {
          height: heightContainer,
          width: widthContainer,
        };
  }

  @Watch('open')
  triggerOpen() {
    this.toggleChat();
  }

  // https://stenciljs.com/docs/component-lifecycle#connectedcallback
  async connectedCallback() {
    this.entityHelper = new PropsComponentHelper(this.el);
    const { assistantId } = this.entityHelper.getProps();

    window.document.addEventListener('chat-assistance-floating-toggle', () => {
      this.toggleChat();
    });

    getProviderConfiguration(assistantId, 'BUBBLE', initParams.sandbox, i18n.getLocale()).then(() => {
      setTimeout(() => {
        window.document.dispatchEvent(new CustomEvent('chat-assistance-floating-loaded'));
      }, 100);
    });
  }

  disconnectedCallback() {
    // Reset store if the component is removed
    window.document.dispatchEvent(new CustomEvent('reset-chat-stores'));
  }

  isFullScreenChat = (canEvaluateOpen: boolean) => {
    return canEvaluateOpen ? this.isMobile && this.isOpen : this.isMobile;
  };

  resolveGlobaPosition = (globaConfig: GlobaConfig | undefined): { right: string; bottom: string } => {
    const baseStyles: { right?: string; bottom?: string } = {};

    if (this.isMobile) {
      if (typeof globaConfig?.position?.mobile?.right === 'string') {
        baseStyles.right = globaConfig.position.mobile.right; // params mobile
      } else if (typeof providerConfState.conf?.globa?.mobile?.position?.right === 'string') {
        baseStyles.right = providerConfState.conf?.globa?.mobile?.position?.right; // HUB mobile
      }

      if (typeof globaConfig?.position?.mobile?.bottom === 'string') {
        baseStyles.bottom = globaConfig.position.mobile.bottom; // params mobile
      } else if (typeof providerConfState.conf?.globa?.mobile?.position?.bottom === 'string') {
        baseStyles.bottom = providerConfState.conf?.globa?.mobile.position.bottom; // HUB mobile
      }
    } else {
      if (typeof globaConfig?.position?.desktop?.right === 'string') {
        baseStyles.right = globaConfig.position.desktop.right; // params desktop
      } else if (typeof providerConfState.conf?.globa?.desktop?.position?.right === 'string') {
        baseStyles.right = providerConfState.conf?.globa?.desktop.position.right; // HUB desktop
      }

      if (typeof globaConfig?.position?.desktop?.bottom === 'string') {
        baseStyles.bottom = globaConfig.position.desktop.bottom; // params desktop
      } else if (typeof providerConfState.conf?.globa?.desktop?.position?.bottom === 'string') {
        baseStyles.bottom = providerConfState.conf?.globa?.desktop.position.bottom; // HUB desktop
      }
    }

    return {
      right: baseStyles.right ?? '10px',
      bottom: baseStyles.bottom ?? '10px',
    };
  };

  resolveGlobaSize = (globaConfig: GlobaConfig | undefined): { height: string; width: string; display: 'flex' } => {
    const baseStyles: { height?: string; width?: string } = {};

    if (this.isMobile) {
      if (typeof globaConfig?.size?.mobile?.height === 'string') {
        baseStyles.height = globaConfig?.size?.mobile?.height;
      } else if (typeof providerConfState.conf?.globa?.mobile?.globaSize?.height === 'string') {
        baseStyles.height = providerConfState.conf?.globa?.mobile.globaSize.height;
      }

      if (typeof globaConfig?.size?.mobile?.width === 'string') {
        baseStyles.width = globaConfig?.size?.mobile?.width;
      } else if (typeof providerConfState.conf?.globa?.mobile?.globaSize?.width === 'string') {
        baseStyles.width = providerConfState.conf?.globa?.mobile.globaSize.width;
      }
    } else {
      if (typeof globaConfig?.size?.desktop?.height === 'string') {
        baseStyles.height = globaConfig?.size?.desktop?.height;
      } else if (typeof providerConfState.conf?.globa?.desktop?.globaSize?.height === 'string') {
        baseStyles.height = providerConfState.conf?.globa?.desktop.globaSize.height;
      }

      if (typeof globaConfig?.size?.desktop?.width === 'string') {
        baseStyles.width = globaConfig?.size?.desktop?.width;
      } else if (typeof providerConfState.conf?.globa?.desktop?.globaSize?.width === 'string') {
        baseStyles.width = providerConfState.conf?.globa?.desktop.globaSize.width;
      }
    }

    return {
      height: baseStyles.height ?? '84px',
      width: baseStyles.width ?? '84px',
      display: 'flex',
    };
  };

  resolveOpenPosition = (globaConfig: GlobaConfig | undefined) => {
    if (this.isMobile) {
      if (typeof globaConfig?.position?.mobile?.opening === 'string') {
        return globaConfig?.position.mobile.opening.toLowerCase();
      } else {
        return providerConfState.conf?.globa?.mobile?.position?.opening?.toLowerCase() ?? 'top';
      }
    } else {
      if (typeof globaConfig?.position?.desktop?.opening === 'string') {
        return globaConfig?.position.desktop.opening.toLowerCase();
      } else {
        return providerConfState.conf?.globa?.desktop?.position?.opening?.toLowerCase() ?? 'top';
      }
    }
  };

  render() {
    const { globa } = this.entityHelper.getProps();
    const isOpenLeft = this.resolveOpenPosition(globa) === 'left';
    const fullScreenChat = this.isFullScreenChat(true);
    const zIndex = this.isOpen ? '2147483640' : '19';
    const hostStyle = fullScreenChat ? {} : this.resolveGlobaPosition(globa);

    return (
      <Host style={{ ...hostStyle, zIndex }} class={{ widget: true, fullscreen: fullScreenChat }}>
        {providerConfState.status === 'DONE' && (
          <Fragment>
            <div id="chat-floating" class={{ 'chat-floating fe-tourpilot-styles': true, 'openTop': !isOpenLeft, 'openLeft': isOpenLeft }}>
              <div class={{ 'chat-header-content-wrapper': true, 'withoutRadius': fullScreenChat }}>
                <div class={'chat-header-closing'}>
                  <div class={'chat-close'}>
                    <i class="eva-3-icon-chevron-down -eva-3-icon-md" onClick={() => this.toggleChat()} />
                  </div>
                </div>
                <div class={{ 'chat-container': true, 'fullscreen': fullScreenChat }} style={this.styles}></div>
              </div>

              {!fullScreenChat && (
                <div class="chat-bubble -eva-3-shadow-static" style={this.resolveGlobaSize(globa)} onClick={() => this.toggleChat()}>
                  <img class="image" src={providerConfState?.conf?.globa?.globaImg} alt={''} />
                </div>
              )}

              {/*Template modals fullscreen*/}
              <Modals store={state} reelsModal={reelsModal} galleryModal={galleryModal} flightDetailModal={flightDetailModal} />
              {/*Toast message*/}
              <Toast show={toast.isOpen} />
            </div>
          </Fragment>
        )}
      </Host>
    );
  }
}
